import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  User, Calendar, IdCard, FileText, Clock, CheckCircle, AlertCircle,
  ChevronRight, Plus, Eye, Download, Bell, Settings, LogOut,
  Stamp, FileCheck, MapPin, Phone, Mail, LayoutDashboard,
  Users, BarChart3, CreditCard, Newspaper, BookOpen, Shield,
  PenTool, Upload, Trash2, Save, Image, X
} from 'lucide-react';

const SERVICE_STATUS = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  processing: { label: 'Em Processamento', color: 'bg-blue-100 text-blue-700', icon: Clock },
  approved: { label: 'Aprovado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  ready: { label: 'Pronto', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Concluído', color: 'bg-gray-100 text-gray-700', icon: CheckCircle },
  rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const CONSULAR_SERVICES = [
  { id: 'appointment', name: 'Agendar Atendimento', icon: Calendar, href: '/agendamento', color: 'bg-blue-500' },
  { id: 'consular_card', name: 'Cartão Consular', icon: IdCard, href: '/cartao-consular', color: 'bg-purple-500' },
  { id: 'salvo_conduto', name: 'Salvo-Conduto', icon: Shield, href: '/salvo-conduto', color: 'bg-teal-500' },
  { id: 'passport', name: 'Passaporte', icon: FileText, href: '/servicos-consulares', color: 'bg-green-500' },
  { id: 'visa', name: 'Pedido de Visto', icon: Stamp, href: '/servicos-consulares', color: 'bg-amber-500' },
  { id: 'legalization', name: 'Legalização de Documentos', icon: FileCheck, href: '/servicos-consulares', color: 'bg-red-500' },
];

const ROLE_LABELS = {
  admin: 'Administrador',
  editor: 'Editor',
  publisher: 'Publicador',
  consular_agent: 'Agente Consular',
  financial: 'Financeiro',
  auditor: 'Auditor',
  consul: 'Cônsul',
  user: 'Utente',
};

function getAdminSections(role) {
  const sections = [];
  // Publicador: dashboard, artigos, revisao, categorias, newsletter
  if (['admin', 'publisher'].includes(role)) {
    sections.push({ name: 'Painel de Controlo', icon: LayoutDashboard, href: '/admin/dashboard', desc: 'Visão geral do sistema' });
    sections.push({ name: 'Artigos', icon: Newspaper, href: '/admin/artigos', desc: 'Gerir artigos e publicações' });
    sections.push({ name: 'Categorias', icon: BookOpen, href: '/admin/categorias', desc: 'Gerir categorias' });
    sections.push({ name: 'Newsletter', icon: Mail, href: '/admin/newsletter', desc: 'Gerir envios de newsletter' });
  }
  // Consul: agendamentos, cartoes-consulares, consul, agente-consular
  if (['admin', 'consul'].includes(role)) {
    sections.push({ name: 'Painel do Cônsul', icon: Shield, href: '/admin/consul', desc: 'Painel executivo do cônsul' });
  }
  // Consul + Agente Consular: agendamentos, cartoes-consulares, agente-consular
  if (['admin', 'consul', 'consular_agent'].includes(role)) {
    sections.push({ name: 'Agendamentos', icon: Calendar, href: '/admin/agendamentos', desc: 'Gerir consultas agendadas' });
    sections.push({ name: 'Cartões Consulares', icon: IdCard, href: '/admin/CardC', desc: 'Gerir pedidos de cartões' });
    sections.push({ name: 'S. Consular', icon: Shield, href: '/admin/aconsular', desc: 'Painel do agente consular' });
  }
  // Financeiro: financeiro, pagamentos
  if (['admin', 'financial'].includes(role)) {
    sections.push({ name: 'Pagamentos', icon: CreditCard, href: '/admin/pagamentos', desc: 'Gerir pagamentos e taxas' });
    sections.push({ name: 'Financeiro', icon: BarChart3, href: '/admin/financeiro', desc: 'Relatórios financeiros' });
  }
  // Admin only
  if (['admin'].includes(role)) {
    sections.push({ name: 'Utilizadores', icon: Users, href: '/admin/utilizadores', desc: 'Gerir contas de utilizadores' });
  }
  return sections;
}

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({ appointments: [], consularCards: [] });

  // Signature states
  const isConsulOrAdmin = user?.role === 'consul' || user?.role === 'admin';
  const [sigMode, setSigMode] = useState('draw');
  const [savedSigUrl, setSavedSigUrl] = useState(user?.consul_signature_url || null);
  const [savingSignature, setSavingSignature] = useState(false);
  const [uploadedSigPreview, setUploadedSigPreview] = useState(null);
  const [uploadedSigFile, setUploadedSigFile] = useState(null);
  const sigCanvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  const isStaff = user && user.role !== 'user';
  const adminSections = user ? getAdminSections(user.role) : [];

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (user?.consul_signature_url) setSavedSigUrl(user.consul_signature_url);
  }, [user?.consul_signature_url]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, cardsRes] = await Promise.all([
        api.get('/appointments/my').catch(() => ({ data: [] })),
        api.get('/consular-cards/my').catch(() => ({ data: [] })),
      ]);
      setData({
        appointments: appointmentsRes.data || [],
        consularCards: cardsRes.data || [],
      });
    } catch (err) {
      setData({ appointments: [], consularCards: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // ===== SIGNATURE CANVAS HANDLERS =====
  const getCanvasCoords = useCallback((e) => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const startSig = useCallback((e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const pos = getCanvasCoords(e);
    lastPosRef.current = pos;
  }, [getCanvasCoords]);

  const drawSig = useCallback((e) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
  }, [getCanvasCoords]);

  const stopSig = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  const clearSig = useCallback(() => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const handleSigUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Apenas PNG ou JPG são aceites.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ficheiro demasiado grande (máx. 2MB).');
      return;
    }
    setUploadedSigFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedSigPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const saveConsulSignature = useCallback(async () => {
    setSavingSignature(true);
    try {
      const formData = new FormData();
      if (sigMode === 'draw') {
        const canvas = sigCanvasRef.current;
        if (!canvas) { toast.error('Canvas não encontrado.'); return; }
        const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
        if (!blob || blob.size < 500) { toast.error('Desenhe a sua assinatura primeiro.'); setSavingSignature(false); return; }
        formData.append('file', blob, 'signature.png');
      } else {
        if (!uploadedSigFile) { toast.error('Selecione um ficheiro de imagem.'); setSavingSignature(false); return; }
        formData.append('file', uploadedSigFile, uploadedSigFile.name);
      }
      const res = await api.post('/auth/consul-signature', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSavedSigUrl(res.data.consul_signature_url);
      setUploadedSigPreview(null);
      setUploadedSigFile(null);
      toast.success('Assinatura guardada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao guardar assinatura.');
    } finally {
      setSavingSignature(false);
    }
  }, [sigMode, uploadedSigFile]);

  const deleteConsulSignature = useCallback(async () => {
    if (!window.confirm('Tem a certeza que deseja remover a sua assinatura?')) return;
    try {
      await api.delete('/auth/consul-signature');
      setSavedSigUrl(null);
      toast.success('Assinatura removida.');
    } catch (err) {
      toast.error('Erro ao remover assinatura.');
    }
  }, []);

  const tabs = [
    { id: 'overview', label: 'Visão Geral' },
    { id: 'appointments', label: 'Meus Agendamentos' },
    { id: 'documents', label: 'Meus Documentos' },
    { id: 'history', label: 'Histórico' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="user-dashboard">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#CC0000] flex items-center justify-center text-xl font-bold">
                {user?.full_name?.[0] || 'U'}
              </div>
              <div>
                <h1 className="text-xl font-bold">Olá, {user?.full_name?.split(' ')[0] || 'Utente'}!</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm text-gray-400">Bem-vindo ao seu portal</span>
                  <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[#CC0000] text-white rounded">
                    {ROLE_LABELS[user?.role] || 'Utente'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/" className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white" data-testid="home-link" title="Página Inicial">
                <MapPin size={20} />
              </Link>
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white" data-testid="logout-btn">
                <LogOut size={18} />
                <span className="text-sm hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Admin/Staff Quick Access */}
        {isStaff && adminSections.length > 0 && (
          <div className="mb-8" data-testid="admin-sections">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield size={18} className="text-[#CC0000]" />
              Área de Gestão
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {adminSections.map(section => {
                const Icon = section.icon;
                return (
                  <Link
                    key={section.href}
                    to={section.href}
                    className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md hover:border-[#CC0000]/30 transition-all group"
                    data-testid={`admin-link-${section.href.replace(/\//g, '-')}`}
                  >
                    <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#CC0000] transition-colors">
                      <Icon size={18} className="text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-[#CC0000] transition-colors">{section.name}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">{section.desc}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Consul Signature Management */}
        {isConsulOrAdmin && (
          <div className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="consul-signature-section">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <PenTool size={18} className="text-[#CC0000]" />
                Assinatura Digital do Chefe Consular
              </h2>
              <p className="text-sm text-gray-500 mt-1">Assinatura que será aposta nos documentos emitidos (Cartões Consulares, Salvo-Condutos, etc.)</p>
            </div>

            <div className="p-5 space-y-5">
              {/* Current signature preview */}
              {savedSigUrl && (
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="current-signature-preview">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-green-800">Assinatura registada</p>
                      <p className="text-xs text-green-600">Clique para atualizar ou remover</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${savedSigUrl}`}
                      alt="Assinatura do Cônsul"
                      className="h-14 border border-green-300 rounded bg-white px-3 py-1"
                      data-testid="saved-signature-image"
                    />
                    <button
                      onClick={deleteConsulSignature}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid="delete-signature-btn"
                      title="Remover assinatura"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              )}

              {/* Mode tabs */}
              <div className="flex gap-2" data-testid="signature-mode-tabs">
                <button
                  onClick={() => setSigMode('draw')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    sigMode === 'draw'
                      ? 'bg-[#0A0A0A] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="sig-mode-draw-btn"
                >
                  <PenTool size={16} /> Desenhar
                </button>
                <button
                  onClick={() => setSigMode('upload')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    sigMode === 'upload'
                      ? 'bg-[#0A0A0A] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="sig-mode-upload-btn"
                >
                  <Upload size={16} /> Carregar Ficheiro
                </button>
              </div>

              {/* Draw mode */}
              {sigMode === 'draw' && (
                <div className="space-y-3" data-testid="sig-draw-area">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-1 bg-gray-50 relative">
                    <canvas
                      ref={sigCanvasRef}
                      width={600}
                      height={180}
                      className="w-full rounded-lg bg-white cursor-crosshair touch-none"
                      style={{ maxHeight: '180px' }}
                      onMouseDown={startSig}
                      onMouseMove={drawSig}
                      onMouseUp={stopSig}
                      onMouseLeave={stopSig}
                      onTouchStart={startSig}
                      onTouchMove={drawSig}
                      onTouchEnd={stopSig}
                      data-testid="sig-canvas"
                    />
                    <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-gray-400 pointer-events-none select-none">
                      Desenhe a sua assinatura aqui
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={clearSig}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      data-testid="clear-canvas-btn"
                    >
                      <X size={14} /> Limpar
                    </button>
                    <button
                      onClick={saveConsulSignature}
                      disabled={savingSignature}
                      className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-[#CC0000] hover:bg-[#A30000] rounded-lg transition-colors disabled:opacity-50"
                      data-testid="save-drawn-sig-btn"
                    >
                      <Save size={14} /> {savingSignature ? 'A guardar...' : 'Guardar Assinatura'}
                    </button>
                  </div>
                </div>
              )}

              {/* Upload mode */}
              {sigMode === 'upload' && (
                <div className="space-y-3" data-testid="sig-upload-area">
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors">
                    {uploadedSigPreview ? (
                      <img
                        src={uploadedSigPreview}
                        alt="Pré-visualização"
                        className="max-h-32 object-contain"
                        data-testid="uploaded-sig-preview"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <Image size={32} />
                        <p className="text-sm">Clique para selecionar imagem</p>
                        <p className="text-xs">PNG ou JPG, máx. 2MB</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      className="hidden"
                      onChange={handleSigUpload}
                      data-testid="sig-file-input"
                    />
                  </label>
                  {uploadedSigPreview && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setUploadedSigPreview(null); setUploadedSigFile(null); }}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        data-testid="remove-uploaded-sig-btn"
                      >
                        <X size={14} /> Remover
                      </button>
                      <button
                        onClick={saveConsulSignature}
                        disabled={savingSignature}
                        className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-[#CC0000] hover:bg-[#A30000] rounded-lg transition-colors disabled:opacity-50"
                        data-testid="save-uploaded-sig-btn"
                      >
                        <Save size={14} /> {savingSignature ? 'A guardar...' : 'Guardar Assinatura'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Consular Services */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Serviços Disponíveis</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CONSULAR_SERVICES.map(service => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.id}
                  to={service.href}
                  className="bg-white rounded-xl border border-gray-200 p-5 text-center hover:shadow-lg hover:border-[#CC0000]/30 transition-all group"
                  data-testid={`service-${service.id}`}
                >
                  <div className={`w-14 h-14 ${service.color} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon size={28} className="text-white" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">{service.name}</p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#CC0000] text-[#CC0000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="#" onClick={() => setActiveTab('appointments')} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center"><Calendar size={24} className="text-white" /></div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-gray-900">{data.appointments.length}</p>
                    <p className="text-sm text-gray-500">Agendamentos</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-300 group-hover:text-gray-500" />
                </div>
              </Link>
              <Link to="#" onClick={() => setActiveTab('documents')} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center"><IdCard size={24} className="text-white" /></div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-gray-900">{data.consularCards.length}</p>
                    <p className="text-sm text-gray-500">Cartões Consulares</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-300 group-hover:text-gray-500" />
                </div>
              </Link>
              <Link to="#" onClick={() => setActiveTab('history')} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center"><FileText size={24} className="text-white" /></div>
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-gray-900">
                      {data.appointments.filter(a => a.status === 'pending').length + data.consularCards.filter(c => c.status !== 'completed').length}
                    </p>
                    <p className="text-sm text-gray-500">Pedidos em Curso</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-300 group-hover:text-gray-500" />
                </div>
              </Link>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Actividade Recente</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {[...data.appointments, ...data.consularCards]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .slice(0, 5)
                  .map((item, idx) => {
                    const statusConfig = SERVICE_STATUS[item.status] || SERVICE_STATUS.pending;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            {item.service_type ? <Calendar size={18} className="text-gray-600" /> : <IdCard size={18} className="text-gray-600" />}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.service_type || item.card_name || 'Serviço'}</p>
                            <p className="text-xs text-gray-500">{new Date(item.created_at).toLocaleDateString('pt-PT')}</p>
                          </div>
                        </div>
                        <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                          <StatusIcon size={12} /> {statusConfig.label}
                        </span>
                      </div>
                    );
                  })}
                {data.appointments.length === 0 && data.consularCards.length === 0 && (
                  <div className="p-8 text-center text-gray-400">
                    <FileText size={40} className="mx-auto mb-3 opacity-30" />
                    <p>Sem actividade recente</p>
                    <Link to="/servicos-consulares" className="text-[#CC0000] text-sm font-medium hover:underline mt-2 inline-block">
                      Solicitar um serviço
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-gradient-to-r from-[#CC0000] to-[#A30000] rounded-xl p-6 text-white">
              <h3 className="font-bold text-lg mb-4">Precisa de Ajuda?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <MapPin size={20} className="text-[#FFD700]" />
                  <div>
                    <p className="text-xs text-red-200">Morada</p>
                    <p className="text-sm">Calle Lagasca, 88, Madrid</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={20} className="text-[#FFD700]" />
                  <div>
                    <p className="text-xs text-red-200">Telefone</p>
                    <p className="text-sm">+34 914 35 64 30</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={20} className="text-[#FFD700]" />
                  <div>
                    <p className="text-xs text-red-200">Email</p>
                    <p className="text-sm">embaixada.espanha@mirex.gov.ao</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Meus Agendamentos</h3>
              <Link to="/agendamento" className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000]" data-testid="new-appointment-btn">
                <Plus size={16} /> Novo Agendamento
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Serviço</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Data/Hora</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Estado</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase px-5 py-3">Acções</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.appointments.map((apt, idx) => {
                    const statusConfig = SERVICE_STATUS[apt.status] || SERVICE_STATUS.pending;
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-5 py-3"><span className="text-sm font-medium text-gray-900">{apt.service_type}</span></td>
                        <td className="px-5 py-3"><span className="text-sm text-gray-600">{apt.date} às {apt.time}</span></td>
                        <td className="px-5 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>{statusConfig.label}</span></td>
                        <td className="px-5 py-3 text-center"><button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Eye size={16} /></button></td>
                      </tr>
                    );
                  })}
                  {data.appointments.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-12 text-center text-gray-400">
                      <Calendar size={40} className="mx-auto mb-3 opacity-30" /><p>Não tem agendamentos</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Meus Documentos e Pedidos</h3>
              <Link to="/cartao-consular" className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000]" data-testid="new-document-btn">
                <Plus size={16} /> Novo Pedido
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Referência</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Tipo</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Data</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Estado</th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase px-5 py-3">Acções</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.consularCards.map((card, idx) => {
                    const statusConfig = SERVICE_STATUS[card.status] || SERVICE_STATUS.pending;
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-5 py-3"><span className="text-sm font-mono text-gray-700">{card.reference_code}</span></td>
                        <td className="px-5 py-3"><span className="text-sm font-medium text-gray-900">{card.card_name}</span></td>
                        <td className="px-5 py-3"><span className="text-sm text-gray-600">{new Date(card.created_at).toLocaleDateString('pt-PT')}</span></td>
                        <td className="px-5 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>{statusConfig.label}</span></td>
                        <td className="px-5 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Eye size={16} /></button>
                            {card.payment_status === 'paid' && (
                              <button className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"><Download size={16} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {data.consularCards.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-12 text-center text-gray-400">
                      <IdCard size={40} className="mx-auto mb-3 opacity-30" /><p>Não tem documentos ou pedidos</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Histórico de Serviços</h3>
            <div className="space-y-4">
              {[...data.appointments, ...data.consularCards]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((item, idx) => {
                  const statusConfig = SERVICE_STATUS[item.status] || SERVICE_STATUS.pending;
                  return (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                        {item.service_type ? <Calendar size={18} className="text-blue-500" /> : <IdCard size={18} className="text-purple-500" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.service_type || item.card_name}</p>
                        <p className="text-xs text-gray-500">
                          {item.reference_code && `Ref: ${item.reference_code} · `}
                          {new Date(item.created_at).toLocaleDateString('pt-PT')}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>{statusConfig.label}</span>
                    </div>
                  );
                })}
              {data.appointments.length === 0 && data.consularCards.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <FileText size={40} className="mx-auto mb-3 opacity-30" />
                  <p>Sem histórico de serviços</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
